/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';
import ContainerTemplate from '../../components/Container';
import generateComponentStyles from '../../utils/styleHelper';
import { replacePlaceholdersWithVariables, sessionDataTransformer, normalizeScreenSettings, migrateSchemeNode, mergeVariableBags } from '../../utils/base';
import { getGlobalVariables } from '../../utils/globalVariableHelper';
import parseJson from '../../utils/json';
import { resolveRootLayout } from '../../utils/rootLayout';
import { ApplicationState } from '../../store';
import Checkbox from '../../components/Checkbox';
import Image from '../../components/Image';
import Input from '../../components/Input';
import Radio from '../../components/Radio';
import Select from '../../components/Select';
import Separator from '../../components/Separator';
import Switcher from '../../components/Switcher';
import Chart from '../../components/Chart';
import Text from '../../components/Text';
import UnknownComponent, { isUnknownComponent } from '../../components/UnknownComponent';

import * as styles from './testComponent.module.scss';
// The published page's root wrapper classes — `.pageWrapColumn` pins column-root children.
import * as pageStyles from '../page/page.module.scss';
import { GridContainer, NodeTypeScheme } from '../../types/base';
import { pageSuccess } from '../../store/page/actions';

const styleElementId = 'custom-css-header-kdlshHhgjhgf';

const componentMapping = {
  checkbox: Checkbox,
  image: Image,
  input: Input,
  radio: Radio,
  select: Select,
  separator: Separator,
  switcher: Switcher,
  chart: Chart,
  text: Text,
};

const InternalFrame: React.FC = () => {
  const [styleApplied, setStyleApplied] = useState(false);
  const config = useSelector((state: ApplicationState) => state.config);
  const page = useSelector((state: ApplicationState) => state.page);
  const { data } = config || {};
  const { editor: editorUrl } = data?.urls || {};
  const { data: pageData2, settings, variableData, styleChunks } = page || {};

  // The SAME bag the page route builds, and for the same reason — see the twin in
  // routes/page/index.tsx.
  //
  // This used to substitute against `settings.variables` alone, which is the author's
  // static screen bag and nothing else. Every constructed component (Select, Checkbox,
  // Radio, Switcher, Table, Calendar) keeps its whole runtime model in `data._<instanceId>`,
  // written by its generated `on_launch` through `$setVariable` — so it lands in
  // `variableData`, never in `settings.variables`. Leaving `variableData` out of the bag
  // meant every `{{_select_1.…}}` / `{{_table_1.…}}` binding stayed unresolved for ever:
  // the repeatables got no rows and rendered their template row once with a literal
  // `{{this.label}}`, the table drew no rows at all, and nothing responded to a click.
  // The components were fine — view mode was reading them through the wrong bag, which is
  // why the very same screen worked when published and not here.
  //
  // `mergeVariableBags` rather than a plain merge, and `globalVariables` included, so this
  // resolves identically to the published page instead of merely resolving.
  const globalVariables = getGlobalVariables();
  const mergedVariables = mergeVariableBags(
    (settings?.variables || {}),
    variableData || {},
    globalVariables || {},
  );
  const pageData = replacePlaceholdersWithVariables(pageData2, mergedVariables);
  // Handed down as `pageSettings`, because that is the bag a repeatable resolves its
  // `source` against (ContainerTemplate reads `pageSettings.variables`) — substituting the
  // tree alone would leave every repeatable empty.
  const newSettings = { ...settings, variables: mergedVariables };
  const { page: ppRaw } = pageData || {};
  // The root is laid out exactly as the published page lays it out — flex or grid from
  // `settings.style`. This frame used to hardcode a `settings.cols × settings.rows` grid,
  // so a flex screen (1×1) showed only its first root section. See `rootLayout.ts`.
  const {
    items: pp, pageBackgroundStyle, wrapStyle, isColumnRoot, topLayerParentStyle,
  } = resolveRootLayout(ppRaw, settings);
  const dispatch = useDispatch();

  const [output, setOutput] = useState<GridContainer>(null);
  const [eventCopy, setLastEventCopy] = useState<MessageEvent>(null);
  // Guards the one-shot frame handshake — see the receiveMessageFromParent handler below.
  const frameSchemaAppliedRef = useRef(false);
  const dispatchContentUpdateEvent = (height: number) => {
    if (eventCopy) {
      const sourceWindow = eventCopy.source as Window;
      sourceWindow.postMessage({ newHeight: height }, eventCopy.origin);
    }
  };

  // Corrected syntax for the type guard function
  const expectedOrigin = editorUrl;

  useEffect(() => {
    setTimeout(() => {
      // const bodyScrollHeight = document.body.scrollHeight;
      const elementId = '6201:298088';
      const element = document.getElementById(elementId);
      if (element) {
        const { scrollHeight } = element;
        dispatchContentUpdateEvent(scrollHeight); // Trigger the event
      }
    }, 1000);
  }, [output]);

  useEffect(() => {
    const receiveMessageFromParent = (event: MessageEvent) => {
      // Accept messages from expectedOrigin, or from window.parent when origin is not yet configured
      const isAllowed = expectedOrigin
        ? event.origin === expectedOrigin
        : event.source === window.parent;

      if (isAllowed) {
        if (event.source) {
          const result = parseJson(event.data);
          const sourceWindow = event.source as Window;
          if (result.success && (result as any)?.data?.page) {
            const responseMessage = 'Parsed JSON';
            // One-shot, same as the page frame handshake: we announce `iframeReady`
            // twice (mount, then again once `expectedOrigin` resolves from the async
            // config), the parent answers both, and a re-applied PAGE_SUCCESS resets
            // `variableData` — discarding what the screen's on_launch handlers wrote.
            if (frameSchemaAppliedRef.current) {
              sourceWindow.postMessage(responseMessage, event.origin);
              return;
            }
            frameSchemaAppliedRef.current = true;
            setLastEventCopy(event);
            setOutput((result as any)?.data?.page as GridContainer);
            dispatch(pageSuccess({
              // Same reason as `settings` below: the posted schema never went through
              // parseOldFormat/parseNewFormat, so the protocol migration runs here — it is
              // what turns the legacy camelCase `activeState` into the `active_state`
              // components read. Without it no node state is applied at all.
              data: migrateSchemeNode((result as any).data.page),
              // Same normalization as the page frame handshake — the editor posts its own
              // working config (stringified `variables`, camelCase resolutions), which never
              // went through parseOldFormat/parseNewFormat. See normalizeScreenSettings.
              settings: normalizeScreenSettings((result as any).data.settings),
              session_data: sessionDataTransformer((result as any).data.session_data || {}),
              variableData: (result as any).data.variableData,
              screenId: (result as any).data.screenId,
            }));
            sourceWindow.postMessage(responseMessage, event.origin);
          } else {
            const responseMessage = 'Failed to parse JSON';
            sourceWindow.postMessage(responseMessage, event.origin);
          }
        }
      }
    };

    // Attach the receiveMessageFromParent function to the message event
    window.addEventListener('message', receiveMessageFromParent);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('message', receiveMessageFromParent);
    };
  }, [expectedOrigin]);

  useEffect(() => {
    const { basic, variables } = data || {};

    // The theme is injected on its own. This used to read
    // `if (basic && variables && classes)` around a builder that turned a global
    // `classes` blob into CSS rules — so whenever that blob was absent the
    // <style> element was never created at all, and every `$token -> var(--token)`
    // reference in every component silently resolved to nothing.
    //
    // Style classes are branch-scoped resources now (`/api/v1/classes`); the blob
    // is gone, and the theme no longer depends on it.
    if (!basic || !variables) return;

    let styleElement = document.getElementById(styleElementId);
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleElementId;
      document.head.insertBefore(styleElement, document.head.firstChild);
    }
    styleElement.innerText = `${variables}\n${basic}`;
  }, [data]);

  useEffect(() => {
    // Notify parent window that iframe is ready to receive messages.
    // Use expectedOrigin when available; fall back to '*' so the signal is
    // always delivered even before config is loaded.
    if (window.parent) {
      window.parent.postMessage({ type: 'iframeReady' }, expectedOrigin || '*');
    }
  }, [expectedOrigin]);

  // FE-4798: bridge the editor's mode switcher. Keydowns inside this frame never
  // reach the parent window, so with focus in the rendered screen ⌥Tab / Ctrl+`
  // — and the modifier release that commits the pick — would be lost. Forwarded
  // as messages; the editor replays them (Iframe.tsx). Only the opening chord is
  // prevented here (Tab would move focus inside the frame); Esc / Enter / arrows
  // are forwarded as they are, and only while a modifier is held.
  useEffect(() => {
    if (!window.parent || window.parent === window) return undefined;

    const forward = (phase: 'down' | 'up', event: KeyboardEvent) => {
      window.parent.postMessage({
        type: 'mode-switcher-key',
        phase,
        key: event.key,
        code: event.code,
        altKey: event.altKey,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        metaKey: event.metaKey,
      }, expectedOrigin || '*');
    };

    const isChord = (event: KeyboardEvent) => !event.metaKey && (
      (event.code === 'Tab' && event.altKey && !event.ctrlKey)
      || (event.code === 'Backquote' && event.ctrlKey && !event.altKey)
    );

    const handleKeydown = (event: KeyboardEvent) => {
      if (isChord(event)) {
        // Typing into a rendered field keeps the browser default, as in the editor.
        const el = document.activeElement as HTMLElement | null;
        const tag = el?.tagName?.toUpperCase();
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
        event.preventDefault();
        forward('down', event);
        return;
      }
      if ((event.altKey || event.ctrlKey)
        && ['Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.code)) {
        forward('down', event);
      }
    };

    const handleKeyup = (event: KeyboardEvent) => {
      if (event.key === 'Alt' || event.key === 'Control') forward('up', event);
    };

    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('keyup', handleKeyup);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('keyup', handleKeyup);
    };
  }, [expectedOrigin]);

  return (
    <div>
      <div>
        <div
        className={styles.frameLayout}
        >
          <div
            style={{
              boxSizing: 'border-box',
            }}
            className={cn({
              [styles.full]: true,
            })}
          >
            <div style={{ overflow: 'auto', height: '100%' }}>
              {pp ? (
                <div className={styles.page} style={generateComponentStyles(pageBackgroundStyle)}>
                  <div
                    className={isColumnRoot ? `${pageStyles.pageWrap} ${pageStyles.pageWrapColumn}` : pageStyles.pageWrap}
                    style={wrapStyle}
                  >
                    {typeof pp === 'object' && Object.keys(pp) && !Array.isArray(pp) ? (
                      <ContainerTemplate 
                       {...pp as NodeTypeScheme} 
                       pageSettings={newSettings} 
                       styleChunks={styleChunks} 
                       topLayerNode
                       parentStyle={topLayerParentStyle}
                       />
                    ) : null}

                    {Array.isArray(pp) ? (
                      <>
                        {pp.map((item) => {
                          const Component = componentMapping[item.component];
                          if (Component) {
                            return <Component key={item.id} {...item} pageSettings={newSettings} styleChunks={styleChunks} topLayerNode parentStyle={topLayerParentStyle} />;
                          } if (item.component === 'container' || item.component === 'table') {
                            return <ContainerTemplate key={item.id} {...(item as NodeTypeScheme)} pageSettings={newSettings} styleChunks={styleChunks} topLayerNode parentStyle={topLayerParentStyle} />;
                          } if (isUnknownComponent(item.component)) {
                            return <UnknownComponent key={item.id} id={item.id} component={item.component} style={item.style} parentStyle={topLayerParentStyle} />;
                          }
                          return null;
                        })}
                      </>
                    ) : null}
                  </div>
                </div>
              ) : <div>No valid data</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternalFrame;
